/**
 * KoszonjukPage — külön köszönő oldal a MEX Magic Mesterkurzus funnelhöz.
 * Route: `/koszonjuk` (public, NEM Layout-wrappelt).
 *
 * Design: a `/webinar` (WebinarLanding) vizuális rendszerével konzisztens,
 * itt picit mélyebb lila/kék árnyalattal, kevesebb rózsaszínnel.
 * A LIVE webinar thank you vizuális rendszerét reuse-olja, de route- és
 * funnel-specifikus tartalommal, dátum / countdown nélkül.
 */
import React, { useEffect } from "react";
import confetti from "canvas-confetti";
import { trackPageView, trackLead, METAPIXEL_NOSCRIPT_URL } from "../lib/metaPixel";
import {
  Sparkle,
  Presentation,
  ChatCircleDots,
  Rocket,
  CalendarBlank,
  Bell,
  EnvelopeOpen,
  Star,
} from "@phosphor-icons/react";

/* ════════════════════════════════════════════════════════════════════
 * TopBar — a mesterkurzus köszönőoldalon dátum és countdown nélkül.
 * ══════════════════════════════════════════════════════════════════ */
function MesterkurzusTopBar() {
  return (
    <header
      data-testid="webinar-thankyou-header"
      className="w-full px-6 sm:px-10 lg:px-16 pt-6 pb-4 relative z-20"
    >
      <div className="max-w-[1280px] mx-auto flex items-center justify-center">
        <a
          href="/mesterkurzus"
          className="flex items-center gap-3.5 justify-center"
          data-testid="webinar-thankyou-logo"
        >
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-[0_12px_32px_rgba(94,80,247,0.36)]"
            style={{ background: "linear-gradient(135deg, #5e50f7 0%, #3a2fd3 100%)" }}
          >
            <Sparkle size={24} weight="fill" color="#fff" />
          </div>
          <div className="font-heading text-[24px] sm:text-[28px] tracking-tight text-[#2B2545] font-bold">
            MEX <span className="text-[#5e50f7]">Magic</span>
          </div>
        </a>
      </div>
    </header>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * Háttér — mélyebb lila/kék, kevesebb rózsaszín, határozottabb kontraszt.
 * ══════════════════════════════════════════════════════════════════ */
function BackgroundLayer() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(160deg, #E7EAF1 0%, #D6DAE6 28%, #C7CCDE 55%, #CDD2E2 78%, #D8DCE9 100%)",
        }}
      />
      {/* Finom rács */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(48,46,102,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(48,46,102,0.06) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 60%, rgba(0,0,0,0) 95%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 60%, rgba(0,0,0,0) 95%)",
        }}
      />
      {/* Indigó glow blob — jobb felső */}
      <div
        className="absolute"
        style={{
          top: "-12%",
          right: "-8%",
          width: "55%",
          height: "72%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(67,56,202,0.42) 0%, rgba(79,70,229,0.16) 42%, rgba(79,70,229,0) 72%)",
          filter: "blur(22px)",
        }}
      />
      {/* Hűvös szteel-kék — bal felső */}
      <div
        className="absolute"
        style={{
          top: "18%",
          left: "-10%",
          width: "42%",
          height: "55%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(56,90,160,0.20) 0%, rgba(56,90,160,0.08) 45%, rgba(56,90,160,0) 75%)",
          filter: "blur(28px)",
        }}
      />
      {/* Mély indigó — alsó középső */}
      <div
        className="absolute"
        style={{
          bottom: "-15%",
          left: "20%",
          width: "55%",
          height: "50%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(49,46,129,0.22) 0%, rgba(49,46,129,0.08) 42%, rgba(49,46,129,0) 72%)",
          filter: "blur(30px)",
        }}
      />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * ExpectItem — vízszintes mini-blokk: [ikon | cím + rövid leírás]
 * 3 oszlopos grid desktopon, egymás alá mobilon.
 * ══════════════════════════════════════════════════════════════════ */
function ExpectItem({ icon: Icon, title, text, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className="flex flex-row items-start gap-5"
    >
      <div
        className="inline-flex items-center justify-center shrink-0"
        style={{
          width: 64,
          height: 64,
          borderRadius: 18,
          background: accent?.background,
          border: accent?.border,
          boxShadow:
            "0 14px 32px -12px rgba(38,32,80,0.45), inset 0 1px 0 rgba(255,255,255,0.75)",
        }}
      >
        <Icon size={32} weight="duotone" color={accent?.iconColor || "#4F46E5"} />
      </div>
      <div className="min-w-0">
        <h3
          className="font-heading mb-2"
          style={{
            fontSize: 19,
            fontWeight: 700,
            color: "#1B1733",
            letterSpacing: "-0.015em",
            lineHeight: 1.25,
          }}
        >
          {title}
        </h3>
        <p style={{ fontSize: 15, lineHeight: 1.6, color: "#3F365E" }}>
          {text}
        </p>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * StepCard — „Következő lépések" glass kártya
 * ══════════════════════════════════════════════════════════════════ */
function StepCard({ title, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl"
      style={{
        background: "rgba(255,255,255,0.92)",
        border: "1px solid rgba(255,255,255,0.95)",
        boxShadow:
          "0 28px 70px -22px rgba(38,32,80,0.32), inset 0 1px 0 rgba(255,255,255,0.9)",
        padding: "24px 24px 22px",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}
    >
      <h3
        className="font-heading mb-3"
        style={{
          fontSize: 17,
          fontWeight: 700,
          color: "#1B1733",
          letterSpacing: "-0.01em",
          lineHeight: 1.3,
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * Felhasználó által feltöltött host fotó (placeholder).
 * Később ki kell cserélni a végleges portréra.
 * ══════════════════════════════════════════════════════════════════ */
const WEBINAR_HOST_PHOTO =
  "https://customer-assets.emergentagent.com/job_mex-foundation/artifacts/geuem7zj_brandagency-render%20%2845%29.png";

/* ════════════════════════════════════════════════════════════════════
 * FŐKOMPONENS
 * ══════════════════════════════════════════════════════════════════ */
export default function KoszonjukPage() {
  // Meta Pixel — konverziós oldal: PageView (egyszer) + Lead (egyszer).
  // A PageView dupla-védelmét a metaPixel helper kezeli (head-consume +
  // _firedPaths), a Lead-et module-szintű guard védi a duplázástól.
  useEffect(() => {
    trackPageView("/koszonjuk");
    trackLead();
  }, []);

  // ═══ NAGY KONFETTI ÁGYÚ ═══════════════════════════════════════════
  // Két-oldali cannon burst a Thank You Page megérkezésekor. Egyszer
  // fut le success-per-session (sessionStorage flag), tiszteletben tartja
  // a prefers-reduced-motion beállítást. Fixed layer, pointer-events: none.
  useEffect(() => {
    try {
      const reduced =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced) return;

      const KEY = "webinar_thankyou_confetti_v1";
      if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(KEY)) return;
      if (typeof sessionStorage !== "undefined") sessionStorage.setItem(KEY, "1");

      const colors = ["#5e50f7", "#7c3aed", "#a78bfa", "#c4b5fd", "#f0abfc", "#22d3ee", "#fbbf24", "#f472b6"];

      // A canvas-confetti default `confetti()` hívása egy globális
      // canvas-t hoz létre fixed layerben, pointer-events: none-nal.
      // Ez layout-safe és nem okoz scrollbart.

      // ═══ KEZDŐ DUPLA BURST — bal + jobb alsó cannon ═══
      // Finomhangolva: kisebb robbanás, gyorsabb esés, nagyobb darabok.
      confetti({
        particleCount: 180,
        angle: 60,
        spread: 85,
        origin: { x: 0.0, y: 0.95 },
        colors,
        startVelocity: 55,
        scalar: 1.35,
        gravity: 1.8,
        ticks: 140,
      });
      confetti({
        particleCount: 180,
        angle: 120,
        spread: 85,
        origin: { x: 1.0, y: 0.95 },
        colors,
        startVelocity: 55,
        scalar: 1.35,
        gravity: 1.8,
        ticks: 140,
      });

      // Kis középső sky-burst — most kisebb, hogy ne legyen túlzó
      confetti({
        particleCount: 60,
        angle: 90,
        spread: 120,
        origin: { x: 0.5, y: 0.75 },
        colors,
        startVelocity: 42,
        scalar: 1.3,
        gravity: 1.8,
        ticks: 140,
      });

      // ═══ Follow-up burst-ök rövidebb ideig ═══
      const duration = 900;
      const end = Date.now() + duration;
      const timer = setInterval(() => {
        if (Date.now() > end) {
          clearInterval(timer);
          return;
        }
        confetti({
          particleCount: 30,
          angle: 60,
          spread: 65,
          origin: { x: Math.random() * 0.3, y: 0.9 },
          colors,
          startVelocity: 45,
          scalar: 1.3,
          gravity: 1.8,
        });
        confetti({
          particleCount: 30,
          angle: 120,
          spread: 65,
          origin: { x: 0.7 + Math.random() * 0.3, y: 0.9 },
          colors,
          startVelocity: 45,
          scalar: 1.3,
          gravity: 1.8,
        });
      }, 150);

      return () => {
        clearInterval(timer);
      };
    } catch {
      /* silent — konfetti nem kritikus */
    }
  }, []);

  return (
    <div
      data-testid="webinar-thankyou-page"
      className="font-sans relative overflow-x-hidden min-h-screen flex flex-col"
      data-font="geist"
      style={{
        background:
          "radial-gradient(60% 50% at 78% 16%, rgba(67,56,202,0.18) 0%, rgba(255,255,255,0) 60%), radial-gradient(50% 40% at 16% 30%, rgba(56,118,180,0.10) 0%, rgba(255,255,255,0) 65%), linear-gradient(180deg, #E5E8F2 0%, #D5D9E8 100%)",
      }}
    >
      <BackgroundLayer />

      <div className="relative z-10 flex-1 flex flex-col">
        <MesterkurzusTopBar />

        <main
          data-testid="webinar-thankyou-main"
          className="flex-1 px-6 sm:px-10 lg:px-16 pt-8 lg:pt-12 pb-0"
        >
          <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-[55%_45%] gap-10 lg:gap-14 items-start">
            {/* ─── BAL OSZLOP (~55%) ─────────────────────────────────── */}
            <div data-testid="webinar-thankyou-left">
              <h1
                className="font-heading lg:whitespace-nowrap"
                data-testid="webinar-thankyou-headline"
                style={{
                  fontSize: "clamp(26px, 2.9vw, 38px)",
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: "-0.03em",
                  color: "#1B1733",
                }}
              >
                Gratulálok! A foglalásod sikeres <span style={{ whiteSpace: "nowrap" }}>volt. 🎉</span>
              </h1>

              <p
                data-testid="webinar-thankyou-subtitle"
                className="mt-4 max-w-[600px]"
                style={{
                  fontSize: "clamp(15px, 1.05vw, 17px)",
                  lineHeight: 1.65,
                  color: "#3F365E",
                }}
              >
                A Mesterkurzus részleteit, a választott időpontot és a saját csatlakozási linkedet hamarosan elküldjük e-mailben. Addig is kövesd az alábbi három lépést, hogy biztosan minden készen álljon.
              </p>

              {/* Host fotó — placeholder a feltöltött képpel */}
              <div className="mt-8 lg:mt-10 relative" data-testid="webinar-thankyou-photo-wrap">
                <div
                  aria-hidden="true"
                  className="absolute -inset-3 rounded-[28px]"
                  style={{
                    background:
                      "radial-gradient(60% 60% at 50% 50%, rgba(79,70,229,0.28) 0%, rgba(76,29,149,0.12) 50%, rgba(94,80,247,0) 80%)",
                    filter: "blur(20px)",
                    zIndex: 0,
                  }}
                />
                <div
                  className="relative rounded-[20px] overflow-hidden"
                  style={{
                    aspectRatio: "16 / 9",
                    background: "#1B1733",
                    border: "1px solid rgba(255,255,255,0.85)",
                    boxShadow:
                      "0 36px 90px -22px rgba(40,32,90,0.45), inset 0 1px 0 rgba(255,255,255,0.65)",
                    zIndex: 1,
                  }}
                  data-testid="webinar-thankyou-photo"
                >
                  <img
                    src={WEBINAR_HOST_PHOTO}
                    alt="Webinar host portré (placeholder)"
                    loading="lazy"
                    decoding="async"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                      display: "block",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* ─── JOBB OSZLOP (~45%) ────────────────────────────────── */}
            <aside data-testid="webinar-thankyou-right" className="lg:pl-2">
              <h2
                className="font-heading mb-7"
                data-testid="webinar-thankyou-steps-heading"
                style={{
                  fontSize: "clamp(22px, 2vw, 28px)",
                  fontWeight: 700,
                  color: "#1B1733",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                }}
              >
                Következő lépések
              </h2>

              <div className="flex flex-col gap-5">
                {/* Kártya 1: Email */}
                <StepCard
                  testid="webinar-thankyou-step-1"
                  title="1. Nézd meg az e-mailedet"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="inline-flex items-center justify-center shrink-0"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background:
                          "linear-gradient(135deg, rgba(94,80,247,0.20) 0%, rgba(76,29,149,0.12) 100%)",
                        border: "1px solid rgba(94,80,247,0.36)",
                      }}
                    >
                      <EnvelopeOpen size={18} weight="duotone" color="#4F46E5" />
                    </div>
                    <p
                      style={{
                        fontSize: 14.5,
                        lineHeight: 1.6,
                        color: "#3F365E",
                      }}
                    >
                      Pár percen belül megérkezik a visszaigazoló e-mail a választott időponttal és a saját csatlakozási linkeddel. Ha nem látod, nézd meg a Promóciók és a Spam mappát is.
                    </p>
                  </div>
                </StepCard>

                {/* Kártya 2: időpont */}
                <StepCard
                  testid="webinar-thankyou-step-2"
                  title="2. Mentsd el a választott időpontot"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="inline-flex items-center justify-center shrink-0"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background:
                          "linear-gradient(135deg, rgba(94,80,247,0.20) 0%, rgba(76,29,149,0.12) 100%)",
                        border: "1px solid rgba(94,80,247,0.36)",
                      }}
                    >
                      <CalendarBlank size={18} weight="duotone" color="#4F46E5" />
                    </div>
                    <p
                      style={{
                        fontSize: 14.5,
                        lineHeight: 1.6,
                        color: "#3F365E",
                      }}
                    >
                      A visszaigazoló e-mailben megtalálod a pontos időpontot. Tedd be rögtön a saját naptáradba, hogy ne csússzon rá más program.
                    </p>
                  </div>
                </StepCard>

                {/* Kártya 3: felkészülés */}
                <StepCard
                  testid="webinar-thankyou-step-3"
                  title="3. Adj magadnak 60 percet"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="inline-flex items-center justify-center shrink-0"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background:
                          "linear-gradient(135deg, rgba(94,80,247,0.20) 0%, rgba(76,29,149,0.12) 100%)",
                        border: "1px solid rgba(94,80,247,0.36)",
                      }}
                    >
                      <Bell size={18} weight="duotone" color="#4F46E5" />
                    </div>
                    <p
                      style={{
                        fontSize: 14.5,
                        lineHeight: 1.6,
                        color: "#3F365E",
                      }}
                    >
                      Készíts elő egy jegyzetfüzetet, tollat és valami innivalót. Lehetőleg számítógépről csatlakozz, és válassz egy nyugodt helyet, ahol végig tudsz figyelni.
                    </p>
                  </div>
                </StepCard>
              </div>
            </aside>
          </div>

          {/* ─── „MIRE SZÁMÍTHATSZ" — TELJES SZÉLESSÉGŰ 3 OSZLOPOS GRID ─── */}
          <section
            data-testid="webinar-thankyou-expect-section"
            className="max-w-[1280px] mx-auto mt-10 lg:mt-14"
          >
            <h2
              data-testid="webinar-thankyou-expect-heading"
              className="font-heading mb-8 lg:mb-10"
              style={{
                fontSize: "clamp(22px, 2vw, 28px)",
                fontWeight: 700,
                color: "#1B1733",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              Mire számíthatsz
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 40,
                width: "100%",
              }}
              className="webinar-thankyou-expect-grid"
            >
              <ExpectItem
                testid="webinar-thankyou-expect-1"
                icon={Presentation}
                title="60 perc fókuszált tudás"
                text="Lépésről lépésre megmutatom, mitől lesz az AI valódi segítség az ügyfélszerzésben, és hol csúszik félre a legtöbb vállalkozó."
                accent={{
                  background: "linear-gradient(135deg, rgba(94,80,247,0.24) 0%, rgba(67,56,202,0.16) 100%)",
                  border: "1px solid rgba(67,56,202,0.42)",
                  iconColor: "#4F46E5",
                }}
              />
              <ExpectItem
                testid="webinar-thankyou-expect-2"
                icon={ChatCircleDots}
                title="Gyakorlati demonstráció"
                text="Nem csak beszélünk róla: működés közben is látni fogod, hogyan lesz a sablonos AI-válaszokból használható, saját hangú marketing."
                accent={{
                  background: "linear-gradient(135deg, rgba(34,211,238,0.22) 0%, rgba(41,190,218,0.12) 100%)",
                  border: "1px solid rgba(41,190,218,0.38)",
                  iconColor: "#159BB5",
                }}
              />
              <ExpectItem
                testid="webinar-thankyou-expect-3"
                icon={Rocket}
                title="Azonnal használható ötletek"
                text="Konkrét gondolatokkal és tisztább iránnyal mész tovább, amit rögtön be tudsz építeni a saját marketingedbe és ügyfélszerzésedbe."
                accent={{
                  background: "linear-gradient(135deg, rgba(251,191,36,0.26) 0%, rgba(251,146,60,0.14) 100%)",
                  border: "1px solid rgba(245,158,11,0.36)",
                  iconColor: "#D97706",
                }}
              />
            </div>
            <style>{`
              @media (max-width: 768px) {
                .webinar-thankyou-expect-grid {
                  grid-template-columns: 1fr !important;
                }
              }
            `}</style>
          </section>

          <section
            data-testid="koszonjuk-business-proof-section"
            className="-mx-6 sm:-mx-10 lg:-mx-16 mt-12 lg:mt-16 px-6 sm:px-10 lg:px-16 pt-20 lg:pt-28 pb-28 lg:pb-40"
            style={{
              background:
                "radial-gradient(70% 55% at 12% 12%, rgba(56,118,180,0.11) 0%, rgba(56,118,180,0) 60%), radial-gradient(75% 55% at 90% 92%, rgba(124,110,230,0.12) 0%, rgba(124,110,230,0) 60%), linear-gradient(180deg, rgba(213,217,232,0) 0px, #F4F4FE 175px, #F4F4FE calc(100% - 175px), #E7E9F5 100%)",
            }}
          >
            <style>{`
              @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;1,400&display=swap');
            `}</style>
            <div className="max-w-[1020px] mx-auto grid grid-cols-1 lg:grid-cols-2">
              {[
                {
                  id: "reka",
                  testid: "koszonjuk-business-proof-reka",
                  name: "Szikora Réka",
                  role: "magántanár mentor",
                  quote:
                    "Az első hónapban a program árának a 3-szorosát kerestem vissza… és 3 hónap alatt a 9-szeresét.",
                },
                {
                  id: "kitti",
                  testid: "koszonjuk-business-proof-kitti",
                  name: "Németh Kitti",
                  role: "transzformációs coach",
                  quote:
                    "7 csoportom ment le eddig, és ez 12 millió forintos bevételt hozott. Nem is gondoltam, hogy ez így lehetséges… de nagyon bejött.",
                },
              ].map((r, idx) => (
                <div
                  key={r.id}
                  data-testid={r.testid}
                  className={
                    idx === 1
                      ? "mt-12 pt-12 border-t lg:mt-0 lg:pt-0 lg:border-t-0 lg:pl-10 lg:border-l lg:flex lg:flex-col"
                      : "lg:pr-10 lg:flex lg:flex-col"
                  }
                  style={idx === 1 ? { borderColor: "rgba(214,169,45,0.45)" } : undefined}
                >
                  <div className="flex items-center" style={{ gap: 4 }}>
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star key={s} size={17} weight="fill" color="#FFDB5C" />
                    ))}
                  </div>
                  <blockquote
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontStyle: "italic",
                      fontSize: "clamp(17px, 1.25vw, 19px)",
                      fontWeight: 400,
                      lineHeight: 1.55,
                      color: "#211C3A",
                      marginTop: 20,
                    }}
                  >
                    „{r.quote}”
                  </blockquote>
                  <div className="hidden lg:block lg:flex-1" aria-hidden="true" />
                  <div
                    aria-hidden="true"
                    style={{ width: 48, height: 1, background: "rgba(214,169,45,0.7)", marginTop: 24 }}
                  />
                  <div style={{ marginTop: 20, fontSize: 15, fontWeight: 600, lineHeight: 1.3, color: "#211C3A" }}>
                    {r.name}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12.5, fontWeight: 400, lineHeight: 1.4, color: "#77718C" }}>
                    {r.role}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </main>

        {/* Lábléc — jogi link sor + Meta disclaimer */}
        <footer
          data-testid="webinar-thankyou-footer"
          className="px-6 sm:px-10 lg:px-16 py-7"
          style={{
            background: "linear-gradient(180deg, #E7E9F5 0%, #DCDEEC 100%)",
            color: "#52587A",
          }}
        >
          <div className="max-w-[1280px] mx-auto flex flex-col gap-3 items-center text-center">
            {/* Jogi link sor */}
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1" style={{ fontSize: 12.5 }}>
              <a href="/aszf" data-testid="footer-link-aszf" className="hover:text-[#4F46E5] transition-colors">
                ÁSZF
              </a>
              <span aria-hidden="true" style={{ opacity: 0.6 }}>·</span>
              <span>MEX Magic • All rights reserved.</span>
              <span aria-hidden="true" style={{ opacity: 0.6 }}>·</span>
              <a href="/adatkezelesi-tajekoztato" data-testid="footer-link-gdpr" className="hover:text-[#4F46E5] transition-colors">
                Adatkezelési tájékoztató
              </a>
            </div>
            {/* Meta disclaimer (megmarad) — 2 sorban, kiegyensúlyozott törés */}
            <div
              className="mx-auto text-center"
              style={{ fontSize: 11.5, lineHeight: 1.55, maxWidth: 720, color: "#7B7596" }}
            >
              <div>
                Ez az oldal nem része a Facebooknak vagy a Meta Platforms Inc.-nek, és nem áll velük kapcsolatban.
              </div>
              <div>
                A Facebook a Meta Platforms Inc. védjegye.
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Meta Pixel — noscript fallback PageView */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src={METAPIXEL_NOSCRIPT_URL}
          alt=""
        />
      </noscript>

    </div>
  );
}
